// src/app/core/services/demo-routing.service.ts
import { Injectable } from '@angular/core';
import { Router, NavigationEnd, Event } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { Location } from '@angular/common';
import { PermissionsService } from '../permissions/permissions.service';
import { LocalStorageService } from './local-storage.service';
import { LocalStorageKeys } from '../constants/local-storage-keys.enum';
import { AgencyStateService } from './agency-state.service';

@Injectable({
  providedIn: 'root'
})
export class DemoRoutingService {
  private isDemoMode = false;
  private isNavigating = false;

  constructor(
    private router: Router,
    private authService: AuthService,
    private location: Location,
    private permissionsService: PermissionsService,
    private localStorage: LocalStorageService,
    private agencyState: AgencyStateService
  ) {}

  /**
   * Post-login landing path. Agency demo lands inside the agency dashboard
   * tree (mirrored under /demo/agency/*); brand demo keeps the historical
   * /demo/dashboard entry point.
   */
  getDemoLandingPath(): string {
    const storedType = this.localStorage.get<string>(LocalStorageKeys.DEMO_TYPE);
    if (storedType === 'agency') {
      return '/demo/agency/dashboard';
    }
    return '/demo/dashboard';
  }

  /**
   * Ініціалізує маршрутизацію для демо-режиму
   * Викликається через APP_INITIALIZER до завантаження додатку
   */
  initDemoRouting(): Promise<boolean> {
    return new Promise(resolve => {
      const currentPath = this.location.path() || '/';
      const isDemoPath = currentPath.startsWith('/demo');

      const isAuthPage =
        currentPath.startsWith('/login') ||
        currentPath.startsWith('/sign-up') ||
        currentPath.startsWith('/auth-success') ||
        currentPath.startsWith('/request-reset-password') ||
        currentPath.startsWith('/reset-password') ||
        currentPath.startsWith('/collaborator-sign-up');

      if (this.authService.isDemoMode() && isAuthPage) {
        this.authService.exitDemoMode();
        resolve(true);
        return;
      }

      if (isDemoPath && !this.authService.isDemoMode() && currentPath !== '/demo-login') {
        // Infer demo type from URL prefix when an unauthenticated visitor
        // deep-links straight into /demo/agency/*; otherwise default to brand.
        const inferredType: 'brand' | 'agency' = currentPath.startsWith('/demo/agency')
          ? 'agency'
          : 'brand';
        this.authService.demoLogin(inferredType).subscribe(() => {
          // Agency deep-link must hydrate `AgencyStateService` BEFORE the
          // app routes render, so the sidebar / header / agency-only guards
          // see `isAgency = true` on first paint. Matches what `DemoGuard`
          // and `DemoLoginComponent` already do for chooser/guard entry.
          // Brand path doesn't need this — keep it fire-and-resolve.
          if (inferredType === 'agency') {
            this.agencyState
              .fetchAndUpdate()
              .then(() => resolve(true))
              .catch(() => resolve(true));
          } else {
            resolve(true);
          }
        });
      } else if (this.authService.isDemoMode() && !isDemoPath) {
        this.permissionsService.setDemoPermissions();
        const demoPath = currentPath === '/' ? this.getDemoLandingPath() : '/demo' + currentPath;
        this.router.navigateByUrl(demoPath, { replaceUrl: true }).then(() => {
          resolve(true);
        });
      } else if (this.authService.isDemoMode() && isDemoPath) {
        this.permissionsService.setDemoPermissions();
        resolve(true);
      } else {
        resolve(true);
      }
    });
  }

  // Метод для виходу з демо-режиму
  exitDemoMode() {
    this.authService.exitDemoMode();

    // Navigate to dashboard if user had a prior session, otherwise login
    const targetPath = this.authService.accessToken ? '/dashboard' : '/login';

    this.isNavigating = true;
    setTimeout(() => {
      this.router.navigateByUrl(targetPath).then(() => {
        this.isNavigating = false;
      });
    }, 100);
  }
}
